/**
 * Resolve an extension's display name, mirroring old CMT
 * (`contractRevisions.js` `setExtensionsNames` L637-651 and
 * `setExtensionsProductNames` L950-966): both start from the extension's
 * `labelKey` and overwrite it *only* from the localized `pricePackageName` map.
 *
 * There is deliberately NO fallback to the technical extension name **on a map miss**:
 * old CMT renders the raw labelKey there (e.g. `02/26OF_Stan_Erw24`), while the technical
 * name (`TarifErw2`) is not substituted for it. AMIE used to insert that fallback, which
 * is what made the two apps disagree (EB-4143).
 *
 * Scoped to "on a map miss" on purpose (PR #42 review, roundup #8): the technical name
 * *can* still reach the screen by two other routes, and the earlier wording claimed it
 * never did.
 *   1. {@link extensionDualName} returns it when there is no readable half at all — the
 *      alternative is a label starting with " (".
 *   2. The revision list shows it in its own right, in parentheses, exactly as the
 *      Individual Pricing column beside it has always done for features
 *      (`revision-list.component.html:75-78`): `Readable (Technical): 1 x 3.90 EUR`, or
 *      `(Technical): …` when no readable name is on file. That is a display decision, not
 *      a labelKey fallback — the two fields stay distinct all the way through.
 *
 * `ContractsService.mapRevision` used to substitute the technical name **into the labelKey
 * slot** when the API sent a booked extension without an `extensionLabelKey`, which made
 * the two indistinguishable downstream — a price package and an extension are different
 * domain objects (the labelKey is versioned per price list: the same `OutboundService`
 * carries `04/25_OutboundService` in one list and `04/25OF_OutboundService` in another).
 * Removed (PR #42 review #8). Old CMT renders **blank** in that case
 * ([`contractRevisions.js:950-968`](https://bitbucket.org/egroupio/customer-management-tool/src/e8410a8/app/scripts/controllers/contractRevisions.js#lines-950:968)
 * seeds `name` from `extensionLabelKey` and overwrites only from the map); showing the
 * technical name instead is a deliberate AMIE improvement, and the same one EB-4006 already
 * made in the dialogs. Measured before deciding: across two price-package catalogs and two
 * contracts (~124 rows) `extensionLabelKey` was never absent, so this path is defensive.
 */
export function extensionDisplayName(
  names: Record<string, string>,
  labelKey: string | null | undefined,
): string {
  const key = labelKey ?? '';
  return (key && names[key]) || key || '';
}

/**
 * "Readable name (technical name)" for selection fields (EB-4006) — the resolved
 * display name plus the technical extension name in parentheses, so operators can
 * find an extension by either. Collapses to just the readable name when there is
 * no separate technical name (or they are identical).
 *
 * NB: old CMT's selection list shows `name (price currency)` and never the
 * technical name (`setExtensionsNames`, showPrice branch). The technical suffix
 * is an AMIE addition from EB-4006 and is kept deliberately; only the readable
 * part follows old CMT.
 */
export function extensionDualName(
  names: Record<string, string>,
  labelKey: string | null | undefined,
  technicalName?: string | null,
): string {
  const readable = extensionDisplayName(names, labelKey);
  const technical = technicalName ?? '';
  // The API payloads are cast without a mapper, so a booked/catalog extension can
  // arrive without a labelKey; without this guard the label would start with " (".
  if (!readable) return technical;
  return technical && technical !== readable ? `${readable} (${technical})` : readable;
}
